/**
 * Context provider for managing and filtering dashboard project.
 * It fetches project metrics based on network and school year dates
 * and provides a mechanism to filter them by site IDs.
 */
import React, { createContext, useContext, useMemo, useState } from 'react';
import useCoreProjectsMetrics from "@/hooks/useCoreProjectsMetrics.js";
import useCoreProjectsMetricsInvitations from "@/hooks/useCoreProjectsMetricsInvitations";

const ProjectsContext = createContext();

export function ProjectsProvider({ children, network, schoolStart, schoolEnd }) {
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);

  const { data: projectData, isLoading: isLoadingProjects } = useCoreProjectsMetrics({
    networkId: network?.id,
    startDate: schoolStart,
    endDate: schoolEnd,
  });
  const { data: invitationData, isLoading: isLoadingInvitations } = useCoreProjectsMetricsInvitations({
    networkId: network?.id,
    startDate: schoolStart,
    endDate: schoolEnd,
  });

  const projects = projectData || [];
  const invitations = invitationData || [];

  const selectedSiteIdSet = useMemo(() => {
    return new Set(selectedSiteIds);
  }, [selectedSiteIds]);

  const filteredProjects = useMemo(() => {
    if (selectedSiteIdSet.size === 0) return projects;
    return projects.filter(project => selectedSiteIdSet.has(project.siteId));
  }, [projects, selectedSiteIdSet]);

  const filteredInvitations = useMemo(() => {
    if (selectedSiteIdSet.size === 0) return invitations;
    return invitations.filter(invitation => selectedSiteIdSet.has(invitation.siteId));
  }, [invitations, selectedSiteIdSet]);

  /**
   * @typedef {Object} ProjectMetricContext
   * @property {ProjectMetricItem[]} projects
   * @property {ProjectMetricItem[]} filteredProjects
   * @property {ProjectMetricInvitationItem[]} invitations
   * @property {ProjectMetricInvitationItem[]} filteredInvitations
   * @property {number[]} selectedSiteIds
   * @property {Function} setSelectedSiteIds
   * @property {boolean} isLoading
   * @property {boolean} isLoadingProjects
   * @property {boolean} isLoadingInvitations
   * @property {object} network
   * @property {string} schoolStart
   * @property {string} schoolEnd
   */
  const value = {
    projects,
    filteredProjects,
    invitations,
    filteredInvitations,
    selectedSiteIds,
    setSelectedSiteIds,
    isLoadingProjects,
    isLoadingInvitations,
    isLoading: isLoadingProjects || isLoadingInvitations,
    network,
    schoolStart,
    schoolEnd
  };

  return (
    <ProjectsContext.Provider value={value}>
      {children}
    </ProjectsContext.Provider>
  );
}

/**
 * @returns {ProjectMetricContext}
 */
export function useProjectsContext() {
  const context = useContext(ProjectsContext);
  if (!context) {
    throw new Error('useProjectsContext must be used within an ProjectsProvider');
  }
  return context;
}
